// Experimente und Feature-Flags im OS-Dashboard. Reine Logik ohne React und
// ohne Netz: Parsen der System-Items, Formular <-> Flag, Validierung,
// Statusübergänge und Laufzeitschätzung.
//
// Verbindliche Spezifikation (Datenformat, Zuteilung, Auswertung):
// aivy-customer-dashboard/src/features/experiments/README.md. Wer Feldnamen
// ändert, ändert sie dort und in allen beteiligten Repos.
import * as Yup from 'yup'

import { FUNNEL_FORM_SOURCE, parseData } from './funnelDropouts'

export const FLAGS_SYSTEM_ID = 'FEATURE_FLAGS'
export const RESULTS_SYSTEM_ID = 'EXPERIMENT_RESULTS'

export const KIND = { ROLLOUT: 'rollout', EXPERIMENT: 'experiment' }

export const STATUS = {
  DRAFT: 'draft',
  RUNNING: 'running',
  PAUSED: 'paused',
  CONCLUDED: 'concluded'
}

export const UNITS = ['visitor', 'partner', 'user']
export const SURFACES = ['lp', 'funnel', 'dashboard']
export const METRICS = [
  'funnel_started',
  'lead_received',
  'account_created',
  'logged_in'
]
export const STAGES = ['develop', 'staging', 'production']
export const LEAD_AMPEL = ['GRUEN', 'GELB', 'ROT']
export const ROLLOUT_VARIANTS = ['off', 'on']

export const KEY_PATTERN = /^[a-z][a-z0-9_]*$/

export const DEFAULT_DECISION = {
  primary: 'lead_received',
  min_days: 14,
  max_days: 42,
  min_exposures_per_variant: 200,
  min_conversions_total: 20,
  // 0.99, weil der Evaluator täglich schaut. 0.95 ergibt dabei rund ein
  // Drittel falsche Gewinner, wenn es keinen echten Unterschied gibt.
  threshold: 0.99
}

export const KIND_LABELS = { rollout: 'Rollout', experiment: 'Experiment' }

export const STATUS_LABELS = {
  draft: 'Entwurf',
  running: 'Läuft',
  paused: 'Pausiert',
  concluded: 'Beendet'
}

export const UNIT_LABELS = {
  visitor: 'Besucher (visitor)',
  partner: 'Partner (partner)',
  user: 'User (user)'
}

export const SURFACE_LABELS = {
  lp: 'Landingpage (lp)',
  funnel: 'Funnel (funnel)',
  dashboard: 'Dashboard (dashboard)'
}

export const METRIC_LABELS = {
  funnel_started: 'Funnel gestartet',
  lead_received: 'Lead eingegangen',
  account_created: 'Account angelegt',
  logged_in: 'Eingeloggt'
}

export const DECISION_STATE_LABELS = {
  collecting: 'Sammelt Daten',
  winner: 'Gewinner steht fest',
  no_effect: 'Kein Effekt',
  manual: 'Auswertung von Hand',
  srm: 'Verteilung auffällig'
}

export const LOCK_NOTICE =
  'Kriterien sind vorab festgelegt, für Änderungen Test beenden und mit neuem Salt neu starten.'

const DAY_MS = 24 * 60 * 60 * 1000

// ---------------------------------------------------------------------------
// System-Items
// ---------------------------------------------------------------------------

const isPlainObject = (value) =>
  !!value && typeof value === 'object' && !Array.isArray(value)

// AWSJSON kommt als String. null heißt "kein gültiges JSON-Objekt".
export const parseSystemData = (raw) => {
  if (raw === null || raw === undefined || raw === '') return {}
  if (isPlainObject(raw)) return raw
  try {
    const parsed = JSON.parse(raw)
    return isPlainObject(parsed) ? parsed : null
  } catch (_) {
    return null
  }
}

// Ergebnis von getSystem(FEATURE_FLAGS) in die Form, mit der die Seite
// arbeitet. parseError sperrt das Speichern, damit ein kaputtes Item nicht
// still überschrieben wird.
export const parseFlagsSystem = (item) => {
  if (!item) {
    return {
      exists: false,
      updatedAt: null,
      data: {},
      flags: {},
      parseError: false
    }
  }

  const data = parseSystemData(item.data)
  if (
    data === null ||
    (data.flags !== undefined && !isPlainObject(data.flags))
  ) {
    return {
      exists: true,
      updatedAt: item.updatedAt || null,
      data: {},
      flags: {},
      parseError: true
    }
  }

  return {
    exists: true,
    updatedAt: item.updatedAt || null,
    data,
    flags: data.flags || {},
    parseError: false
  }
}

export const parseResultsSystem = (item) => {
  const data = parseSystemData(item?.data) || {}
  return {
    exists: !!item,
    updatedAt: data.updated_at || null,
    experiments: isPlainObject(data.experiments) ? data.experiments : {}
  }
}

// Der Map-Key ist maßgeblich, flag.key ist nur eine Kopie davon.
export const flagList = (flags = {}) =>
  Object.entries(flags)
    .filter(([, flag]) => isPlainObject(flag))
    .map(([key, flag]) => ({ ...flag, key }))
    .sort((a, b) => a.key.localeCompare(b.key))

export const resultFor = (results, key) => results?.experiments?.[key] || null

// Schreibanfrage für FEATURE_FLAGS. Existiert das Item noch nicht, wird es
// angelegt. Sonst gilt der Stand beim Laden als Bedingung: Hat der Evaluator
// oder jemand anderes inzwischen geschrieben, schlägt das Update fehl statt
// die Änderung zu überschreiben.
export const systemWriteRequest = (base, nextFlags) => {
  if (!base || base.parseError) {
    throw new Error(
      'FEATURE_FLAGS enthält kein gültiges JSON, Speichern ist gesperrt'
    )
  }

  const data = JSON.stringify({ ...base.data, flags: nextFlags })

  if (!base.exists) {
    return {
      mutation: 'createSystem',
      input: { id: FLAGS_SYSTEM_ID, type: FLAGS_SYSTEM_ID, data }
    }
  }

  return {
    mutation: 'updateSystem',
    input: { id: FLAGS_SYSTEM_ID, data },
    condition: {
      updatedAt: base.updatedAt
        ? { eq: base.updatedAt }
        : { attributeExists: false }
    }
  }
}

// AppSync meldet die verletzte Bedingung als
// DynamoDB:ConditionalCheckFailedException. Bei createSystem auf ein schon
// vorhandenes Item kommt derselbe Fehler.
export const isConditionalCheckFailed = (err) =>
  (err?.errors || []).some(
    (e) =>
      /ConditionalCheckFailed/i.test(e?.errorType || '') ||
      /conditional request failed/i.test(e?.message || '')
  )

// ---------------------------------------------------------------------------
// Flags
// ---------------------------------------------------------------------------

export const variantKeys = (flag) =>
  flag?.kind === KIND.ROLLOUT
    ? ROLLOUT_VARIANTS
    : (flag?.variants || []).map((v) => v.key)

export const controlOf = (flag) => variantKeys(flag)[0] ?? null

// Vorab-Festlegung: Ab dem Start gelten Varianten, Gewichte, Salt und
// Entscheidungskriterien als festgelegt. Pausieren hebt das nicht auf, sonst
// ließen sich die Kriterien per Pause und Wiederaufnahme umgehen. Für
// Rollouts gilt das nicht.
export const isLocked = (flag) =>
  flag?.kind === KIND.EXPERIMENT &&
  (flag.status === STATUS.RUNNING || flag.status === STATUS.PAUSED)

export const normalizedWeights = (variants = []) => {
  const weights = variants.map((v) => Math.max(0, Number(v.weight) || 0))
  const total = weights.reduce((sum, w) => sum + w, 0)
  return variants.map((v, i) => ({
    key: v.key,
    share: total ? weights[i] / total : 0
  }))
}

export const daysRunning = (flag, now = new Date()) => {
  if (!flag?.started_at) return null
  const start = new Date(flag.started_at)
  if (Number.isNaN(start.getTime())) return null
  const end =
    flag.status === STATUS.CONCLUDED && flag.concluded_at
      ? new Date(flag.concluded_at)
      : now
  return Math.max(0, Math.floor((end - start) / DAY_MS))
}

// ---------------------------------------------------------------------------
// Statusübergänge
// ---------------------------------------------------------------------------

export const ACTIONS = {
  START: 'start',
  PAUSE: 'pause',
  RESUME: 'resume',
  CONCLUDE: 'conclude',
  RESTART: 'restart'
}

const TRANSITIONS = {
  [STATUS.DRAFT]: [ACTIONS.START],
  [STATUS.RUNNING]: [ACTIONS.PAUSE, ACTIONS.CONCLUDE],
  [STATUS.PAUSED]: [ACTIONS.RESUME, ACTIONS.CONCLUDE],
  [STATUS.CONCLUDED]: [ACTIONS.RESTART]
}

export const allowedActions = (flag) =>
  TRANSITIONS[flag?.status || STATUS.DRAFT] || []

export const actionLabel = (action, flag) => {
  switch (action) {
    case ACTIONS.START:
      return 'Starten'
    case ACTIONS.PAUSE:
      return 'Pausieren'
    case ACTIONS.RESUME:
      return 'Wieder aufnehmen'
    case ACTIONS.CONCLUDE:
      return 'Beenden'
    case ACTIONS.RESTART:
      return flag?.kind === KIND.EXPERIMENT ? 'Neu starten' : 'Wieder aufnehmen'
    default:
      return action
  }
}

export const currentSalt = (flag) => flag?.salt || flag?.key || ''

// Vorschlag für den Salt beim Neustart: foo -> foo_v2, foo_v2 -> foo_v3.
export const suggestNextSalt = (flag) => {
  const salt = currentSalt(flag)
  const match = salt.match(/^(.*)_v(\d+)$/)
  if (match) return `${match[1]}_v${Number(match[2]) + 1}`
  return `${salt}_v2`
}

/**
 * Wendet eine Statusaktion an und gibt das neue Flag zurück.
 *
 * - start/resume: running, started_at nur setzen, wenn leer
 * - pause: paused
 * - conclude: concluded, winner (muss eine Variante sein), concluded_at
 * - restart (nur aus concluded): running, winner und concluded_at leeren,
 *   neue Laufzeit ab jetzt. Experimente brauchen einen neuen Salt, sonst
 *   landen alle wieder in derselben Gruppe.
 */
export const applyStatusAction = (
  flag,
  action,
  { now = new Date(), winner, salt } = {}
) => {
  if (!allowedActions(flag).includes(action)) {
    throw new Error(
      `Aktion ${action} ist im Status ${flag?.status || STATUS.DRAFT} nicht möglich`
    )
  }

  const iso = now.toISOString()

  // Gleiche Prüfung wie im Modal, hier aber verbindlich: ein Flag, das direkt
  // in Dynamo oder per manipuliertem Submit kaputt ist, startet nicht.
  if (RUNNING_ACTIONS.includes(action)) {
    const problems = flagValidationErrors(flag)
    if (problems.length) throw new Error(problems[0])
  }

  switch (action) {
    case ACTIONS.START:
    case ACTIONS.RESUME:
      return {
        ...flag,
        status: STATUS.RUNNING,
        started_at: flag.started_at || iso
      }
    case ACTIONS.PAUSE:
      return { ...flag, status: STATUS.PAUSED }
    case ACTIONS.CONCLUDE:
      if (!variantKeys(flag).includes(winner)) {
        throw new Error('Bitte einen Gewinner aus den Varianten wählen')
      }
      return {
        ...flag,
        status: STATUS.CONCLUDED,
        winner,
        concluded_at: iso
      }
    case ACTIONS.RESTART: {
      const next = {
        ...flag,
        status: STATUS.RUNNING,
        winner: null,
        concluded_at: null,
        // Altlast des Evaluators aus dem letzten Lauf
        conclusion: null,
        started_at: iso
      }
      if (flag.kind === KIND.EXPERIMENT) {
        const nextSalt = (salt || '').trim()
        if (!nextSalt || nextSalt === currentSalt(flag)) {
          throw new Error('Für den Neustart brauchst du einen neuen Salt')
        }
        next.salt = nextSalt
      }
      return next
    }
    default:
      throw new Error(`Unbekannte Aktion ${action}`)
  }
}

// ---------------------------------------------------------------------------
// Tägliche Auswertung (CronTTL)
// ---------------------------------------------------------------------------

// Der Evaluator hängt an einer CronTTL-Kette. Läuft das Item mit job
// EXPERIMENT_EVALUATION ab, legt cronTTLWorker das nächste an und startet den
// Evaluator. Beim Start eines Experiments wird das nächste Item angelegt,
// damit die Kette sicher existiert. Gibt es das Item schon, ist nichts zu tun.
export const EVALUATION_JOB = 'EXPERIMENT_EVALUATION'
export const EVALUATION_HOUR_UTC = 7

// Aktionen, nach denen das Flag läuft.
export const RUNNING_ACTIONS = [ACTIONS.START, ACTIONS.RESUME, ACTIONS.RESTART]

export const needsEvaluationCron = (flag, action) =>
  flag?.kind === KIND.EXPERIMENT && RUNNING_ACTIONS.includes(action)

// Heute 07:00 UTC, wenn das noch nicht vorbei ist, sonst morgen 07:00 UTC.
export const nextEvaluationTime = (now = new Date()) => {
  const at = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      EVALUATION_HOUR_UTC
    )
  )
  if (at.getTime() <= now.getTime()) at.setUTCDate(at.getUTCDate() + 1)
  return at
}

export const evaluationCronInput = (now = new Date()) => {
  const at = nextEvaluationTime(now)
  return {
    id: `${EVALUATION_JOB}#${at.toISOString().slice(0, 10)}`,
    source_table: 'System',
    source_id: EVALUATION_JOB,
    job: EVALUATION_JOB,
    ttl: Math.floor(at.getTime() / 1000)
  }
}

export const isAlreadyExistsError = (err) =>
  isConditionalCheckFailed(err) ||
  (err?.errors || []).some((e) => /already exists/i.test(e?.message || ''))

// ---------------------------------------------------------------------------
// Formular <-> Flag
// ---------------------------------------------------------------------------

const DEFAULT_VARIANTS = [
  { key: 'control', weight: 50 },
  { key: '', weight: 50 }
]

export const emptyFormValues = () => ({
  key: '',
  description: '',
  owner: '',
  kind: KIND.EXPERIMENT,
  unit: 'visitor',
  surface: 'funnel',
  salt: '',
  variants: DEFAULT_VARIANTS.map((v) => ({ ...v })),
  percent: 0,
  targeting: { stages: [], partner_ids: '', lead_ampel: [], is_trial: '' },
  overrides: [],
  decision: { ...DEFAULT_DECISION }
})

const clampPercent = (value) => Math.min(100, Math.max(0, Number(value) || 0))

export const flagToFormValues = (flag = {}, key = flag.key) => {
  const empty = emptyFormValues()
  const targeting = flag.targeting || {}
  return {
    ...empty,
    key: key || '',
    description: flag.description || '',
    owner: flag.owner || '',
    kind: flag.kind === KIND.ROLLOUT ? KIND.ROLLOUT : KIND.EXPERIMENT,
    unit: flag.unit || empty.unit,
    surface: flag.surface || empty.surface,
    salt: flag.salt || key || '',
    variants: flag.variants?.length
      ? flag.variants.map((v) => ({
          key: v.key || '',
          weight: Number(v.weight) || 0
        }))
      : empty.variants,
    percent: clampPercent(flag.percent),
    targeting: {
      stages: Array.isArray(targeting.stages) ? targeting.stages : [],
      partner_ids: Array.isArray(targeting.partner_ids)
        ? targeting.partner_ids.join('\n')
        : '',
      lead_ampel: Array.isArray(targeting.lead_ampel)
        ? targeting.lead_ampel
        : [],
      is_trial:
        typeof targeting.is_trial === 'boolean'
          ? String(targeting.is_trial)
          : ''
    },
    overrides: Object.entries(flag.overrides || {}).map(
      ([unitId, variant]) => ({ unitId, variant })
    ),
    decision: { ...DEFAULT_DECISION, ...(flag.decision || {}) }
  }
}

export const splitList = (text = '') => [
  ...new Set(
    String(text)
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean)
  )
]

export const cleanTargeting = (targeting = {}) => {
  const out = {}
  if (targeting.stages?.length) out.stages = [...targeting.stages]
  const partnerIds = splitList(targeting.partner_ids)
  if (partnerIds.length) out.partner_ids = partnerIds
  if (targeting.lead_ampel?.length) out.lead_ampel = [...targeting.lead_ampel]
  if (targeting.is_trial === 'true' || targeting.is_trial === 'false') {
    out.is_trial = targeting.is_trial === 'true'
  }
  return out
}

const toInt = (value) => Math.round(Number(value))

export const cleanDecision = (decision = {}) => ({
  primary: decision.primary,
  min_days: toInt(decision.min_days),
  max_days: toInt(decision.max_days),
  min_exposures_per_variant: toInt(decision.min_exposures_per_variant),
  min_conversions_total: toInt(decision.min_conversions_total),
  threshold: Number(decision.threshold)
})

const KNOWN_FIELDS = [
  'key',
  'description',
  'owner',
  'kind',
  'status',
  'unit',
  'surface',
  'salt',
  'variants',
  'percent',
  'targeting',
  'overrides',
  'winner',
  'decision',
  'started_at',
  'concluded_at'
]

/**
 * Baut aus den Formularwerten das zu speichernde Flag. Status, Laufzeit und
 * Gewinner kommen immer aus `base` (die ändern nur die Statusaktionen).
 * Ist `base` gesperrt (laufendes oder pausiertes Experiment), bleiben Art,
 * Unit, Salt, Varianten und decision unverändert, egal was im Formular steht.
 * Unbekannte Felder aus `base` bleiben erhalten.
 */
export const formValuesToFlag = (values, base = null) => {
  const prev = base || {}
  const locked = isLocked(prev)
  const key = prev.key || values.key.trim()
  const kind = locked ? prev.kind : values.kind

  const flag = {
    key,
    description: (values.description || '').trim(),
    owner: (values.owner || '').trim(),
    kind,
    status: prev.status || STATUS.DRAFT,
    unit: locked ? prev.unit : values.unit,
    // Oberflaeche wechseln hiesse Zuteilung und Exposures woanders hin
    surface: locked ? prev.surface : values.surface,
    salt: locked ? prev.salt || key : (values.salt || '').trim() || key
  }

  if (kind === KIND.EXPERIMENT) {
    flag.variants = locked
      ? prev.variants
      : values.variants.map((v) => ({
          key: v.key.trim(),
          weight: Number(v.weight)
        }))
  } else {
    flag.percent = clampPercent(values.percent)
  }

  flag.targeting = cleanTargeting(values.targeting)
  flag.overrides = Object.fromEntries(
    (values.overrides || [])
      .filter((o) => (o.unitId || '').trim())
      .map((o) => [o.unitId.trim(), o.variant])
  )
  flag.winner = prev.winner ?? null

  if (kind === KIND.EXPERIMENT) {
    flag.decision = locked ? prev.decision : cleanDecision(values.decision)
  }

  flag.started_at = prev.started_at ?? null
  flag.concluded_at = prev.concluded_at ?? null

  const extras = Object.fromEntries(
    Object.entries(prev).filter(([field]) => !KNOWN_FIELDS.includes(field))
  )

  return { ...flag, ...extras }
}

// ---------------------------------------------------------------------------
// Validierung
// ---------------------------------------------------------------------------

const KEY_MESSAGE =
  'Nur Kleinbuchstaben, Ziffern und Unterstrich, am Anfang ein Buchstabe'

const intField = (min, message) =>
  Yup.number()
    .typeError('Bitte eine Zahl eingeben')
    .required('Pflichtfeld')
    .integer('Bitte eine ganze Zahl eingeben')
    .min(min, message)

const decisionSchema = Yup.object({
  primary: Yup.string()
    .oneOf(METRICS, 'Bitte eine Metrik wählen')
    .required('Bitte eine Metrik wählen'),
  min_days: intField(1, 'Mindestens 1 Tag'),
  max_days: intField(1, 'Mindestens 1 Tag').min(
    Yup.ref('min_days'),
    'max_days darf nicht kleiner als min_days sein'
  ),
  min_exposures_per_variant: intField(1, 'Mindestens 1'),
  min_conversions_total: intField(1, 'Mindestens 1'),
  threshold: Yup.number()
    .typeError('Bitte eine Zahl eingeben')
    .required('Pflichtfeld')
    .moreThan(0.5, 'Muss größer als 0,5 sein')
    .lessThan(1, 'Muss kleiner als 1 sein')
})

const variantsSchema = Yup.array()
  .of(
    Yup.object({
      key: Yup.string()
        .trim()
        .required('Key fehlt')
        .matches(KEY_PATTERN, KEY_MESSAGE),
      weight: Yup.number()
        .typeError('Bitte eine Zahl eingeben')
        .required('Gewicht fehlt')
        .moreThan(0, 'Gewicht muss größer als 0 sein')
    })
  )
  .min(2, 'Ein Experiment braucht mindestens zwei Varianten')
  .test(
    'unique-keys',
    'Jeder Varianten-Key darf nur einmal vorkommen',
    (list) => {
      const keys = (list || [])
        .map((v) => (v?.key || '').trim())
        .filter(Boolean)
      return new Set(keys).size === keys.length
    }
  )

/**
 * @param {object} opts
 * @param {string[]} opts.existingKeys Keys, die es schon gibt (nur beim Anlegen relevant)
 * @param {boolean} opts.isNew Anlegen statt Bearbeiten
 * @param {boolean} opts.locked Vorab-Festlegung aktiv, gesperrte Felder nicht prüfen
 */
export const buildFlagSchema = ({
  existingKeys = [],
  isNew = true,
  locked = false
} = {}) =>
  Yup.object({
    key: isNew
      ? Yup.string()
          .trim()
          .required('Key fehlt')
          .matches(KEY_PATTERN, KEY_MESSAGE)
          .notOneOf(existingKeys, 'Diesen Key gibt es schon')
      : Yup.string(),
    description: Yup.string(),
    owner: Yup.string().trim().required('Owner fehlt'),
    kind: Yup.string().oneOf(Object.values(KIND)).required(),
    unit: Yup.string().oneOf(UNITS, 'Bitte eine Unit wählen').required(),
    surface: Yup.string()
      .oneOf(SURFACES, 'Bitte eine Surface wählen')
      .required(),
    salt: Yup.string().trim(),
    variants: Yup.array().when('kind', {
      is: (kind) => kind === KIND.EXPERIMENT && !locked,
      then: () => variantsSchema,
      otherwise: (schema) => schema
    }),
    percent: Yup.mixed().when('kind', {
      is: KIND.ROLLOUT,
      then: () =>
        Yup.number()
          .typeError('Bitte eine Zahl eingeben')
          .required('Pflichtfeld')
          .min(0, 'Mindestens 0')
          .max(100, 'Höchstens 100'),
      otherwise: (schema) => schema
    }),
    overrides: Yup.array()
      .of(
        Yup.object({
          unitId: Yup.string().trim().required('Unit-ID fehlt'),
          variant: Yup.string().required('Bitte eine Variante wählen')
        })
      )
      .test(
        'unique-units',
        'Jede Unit-ID darf nur einmal vorkommen',
        (list) => {
          const ids = (list || [])
            .map((o) => (o?.unitId || '').trim())
            .filter(Boolean)
          return new Set(ids).size === ids.length
        }
      )
      .test(
        'known-variant',
        'Ein Override zeigt auf eine Variante, die es nicht gibt',
        function (list) {
          const keys =
            this.parent.kind === KIND.ROLLOUT
              ? ROLLOUT_VARIANTS
              : (this.parent.variants || []).map((v) => (v?.key || '').trim())
          return (list || []).every(
            (o) => !o?.variant || keys.includes(o.variant)
          )
        }
      ),
    decision: Yup.object().when('kind', {
      is: (kind) => kind === KIND.EXPERIMENT && !locked,
      then: () => decisionSchema,
      otherwise: (schema) => schema
    })
  })

// EXPERIMENT_RESULTS kann kaputt oder alt sein, die Seite darf daran nie
// scheitern.
export const asArray = (value) => (Array.isArray(value) ? value : [])

// Ergebnis stammt aus einem Lauf vor dem aktuellen Start (z. B. nach einem
// Neustart, bevor der Evaluator wieder gelaufen ist).
export const isResultStale = (flag, result) =>
  !!(
    result?.evaluated_at &&
    flag?.started_at &&
    Date.parse(result.evaluated_at) < Date.parse(flag.started_at)
  )

// Prüft ein gespeichertes Flag (z. B. vor dem Start). Leere Liste = in Ordnung.
export const flagValidationErrors = (flag) => {
  try {
    buildFlagSchema({ isNew: false, locked: false }).validateSync(
      flagToFormValues(flag),
      { abortEarly: false }
    )
    return []
  } catch (err) {
    return [...new Set(err.errors || [err.message])]
  }
}

// ---------------------------------------------------------------------------
// Laufzeitschätzung
// ---------------------------------------------------------------------------

// z-Werte für alpha 0,05 zweiseitig und Power 0,8.
export const Z_ALPHA = 1.959963984540054
export const Z_POWER = 0.8416212335729143

/**
 * Benötigte Units pro Variante für den Vergleich Control gegen eine Variante
 * (Zwei-Proportionen-Test, gleiche Gruppengröße, ohne Stetigkeitskorrektur).
 *
 * @param {number} baseline Conversionrate von Control als Anteil (0,05 = 5 %)
 * @param {number} relativeEffect minimal relevanter Effekt relativ (0,5 = +50 %)
 * @returns {number|null} null bei unbrauchbaren Eingaben
 */
export const sampleSizePerVariant = ({ baseline, relativeEffect }) => {
  const p1 = Number(baseline)
  const effect = Number(relativeEffect)
  const p2 = p1 * (1 + effect)
  if (!(p1 > 0 && p1 < 1) || !effect || !(p2 > 0 && p2 < 1)) return null

  const pBar = (p1 + p2) / 2
  const a = Z_ALPHA * Math.sqrt(2 * pBar * (1 - pBar))
  const b = Z_POWER * Math.sqrt(p1 * (1 - p1) + p2 * (1 - p2))
  return Math.ceil((a + b) ** 2 / (p2 - p1) ** 2)
}

/**
 * Geschätzte Laufzeit. Die kleinste Variante bestimmt die Dauer, die
 * Mindestkriterien des Evaluators (min_days, min_exposures_per_variant)
 * gelten zusätzlich.
 */
export const estimateRuntime = ({
  baseline,
  relativeEffect,
  dailyTraffic,
  variants = [],
  decision = {}
}) => {
  const perVariant = sampleSizePerVariant({ baseline, relativeEffect })
  if (!perVariant) return null

  const shares = normalizedWeights(variants).map((v) => v.share)
  const minShare =
    shares.length && shares.every((s) => s > 0)
      ? Math.min(...shares)
      : 1 / Math.max(2, shares.length)

  const needed = Math.max(
    perVariant,
    Number(decision.min_exposures_per_variant) || 0
  )
  const totalUnits = Math.ceil(needed / minShare)
  const traffic = Number(dailyTraffic)

  if (!(traffic > 0)) {
    return {
      perVariant,
      totalUnits,
      days: null,
      daysForSample: null,
      exceedsMaxDays: false
    }
  }

  const daysForSample = Math.ceil(needed / (traffic * minShare))
  const days = Math.max(daysForSample, Number(decision.min_days) || 0)
  const maxDays = Number(decision.max_days) || 0

  return {
    perVariant,
    totalUnits,
    days,
    daysForSample,
    exceedsMaxDays: maxDays > 0 && days > maxDays
  }
}

export const TRAFFIC_WINDOW_DAYS = 14

/**
 * Funnel-Starts der letzten 14 Tage als Basis für den Schätzer. Jeder
 * Funnel-Start legt eine Form an (status IN_PROGRESS), im Aivy OS angelegte
 * Kunden zählen nicht als Traffic.
 */
export const funnelStatsFromForms = (
  forms = [],
  now = new Date(),
  days = TRAFFIC_WINDOW_DAYS
) => {
  const from = now.getTime() - days * DAY_MS
  const recent = forms.filter(
    (form) =>
      form.form_source === FUNNEL_FORM_SOURCE &&
      new Date(form.createdAt).getTime() >= from &&
      !parseData(form.data).createdViaAdmin
  )
  const starts = recent.length
  const rate = (count) => (starts ? count / starts : null)

  return {
    starts,
    days,
    perDay: starts / days,
    rates: {
      funnel_started: null,
      lead_received: rate(
        recent.filter((f) => f.status && f.status !== 'IN_PROGRESS').length
      ),
      account_created: rate(recent.filter((f) => f.partner_id).length),
      logged_in: null
    }
  }
}

// ---------------------------------------------------------------------------
// Anzeige
// ---------------------------------------------------------------------------

export const formatNumber = (value, digits = 0) =>
  value === null || value === undefined || Number.isNaN(Number(value))
    ? '-'
    : new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits
      }).format(value)

export const formatPercent = (share, digits = 1) =>
  share === null || share === undefined || Number.isNaN(Number(share))
    ? '-'
    : `${formatNumber(share * 100, digits)} %`

export const describeAllocation = (flag) => {
  if (flag?.kind === KIND.ROLLOUT) {
    return `on ${formatNumber(clampPercent(flag.percent))} %`
  }
  return normalizedWeights(flag?.variants || [])
    .map((v) => `${v.key} ${formatNumber(v.share * 100)} %`)
    .join(' · ')
}

// Reason-Codes des experimentEvaluator (Liste in der README der Spezifikation).
// Unbekannte Codes erscheinen unverändert.
export const describeDecisionReason = (decision, flag) => {
  const reason = decision?.reason
  const d = flag?.decision || {}
  switch (reason) {
    case undefined:
    case null:
    case '':
      return null
    case 'min_days':
      return `Mindestlaufzeit von ${d.min_days ?? '?'} Tagen noch nicht erreicht`
    case 'min_exposures':
      return `Noch nicht ${d.min_exposures_per_variant ?? '?'} Exposures je Variante`
    case 'min_conversions':
      return `Noch nicht ${d.min_conversions_total ?? '?'} Conversions insgesamt`
    case 'threshold_reached':
      return `Eine Variante hat P(best) von mindestens ${formatPercent(d.threshold, 1)}`
    case 'below_threshold':
      return `Keine Variante erreicht P(best) von ${formatPercent(d.threshold, 1)}`
    case 'max_days_reached':
      return `Maximale Laufzeit von ${d.max_days ?? '?'} Tagen erreicht, kein Gewinner, es bleibt Control`
    case 'unit_not_visitor':
      return 'Unit ist nicht visitor, die Auswertung passiert von Hand'
    case 'less_than_two_variants':
      return 'Weniger als zwei Varianten, nichts zu vergleichen'
    case 'sample_ratio_mismatch':
      return 'Die Verteilung der Besucher passt nicht zu den Gewichten. Zuteilung und Exposure-Tracking prüfen, bis dahin wird nichts entschieden'
    default:
      return reason
  }
}
